import { useTicketSummary } from '../support/queries';
import { Activity, Building2, Check, ChevronsUpDown, ClipboardList, FileCode2, Keyboard, LogOut, Menu, Monitor, Moon, ScrollText, Search, Settings, Sun, SunMoon, UserRound, X } from 'lucide-react';
import { Dialog, DropdownMenu } from 'radix-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import { useManagesEndUsers } from '../customers/AddCustomer';
import { useMissingSources } from '../lib/sources';
import { absolute } from '../lib/time';
import { type Theme, useTheme } from '../lib/theme';
import { useScope, useWorkspaces } from '../scope/scope';
import { fleetQuery, needsLookQuery, useCatalog, useFacets, useJobs, useSearch } from '../search/hooks';
import { useSession } from '../session/session';
import { cn } from '../ui/cn';
import { Kbd, Logo, modKey } from '../ui/misc';
import { CommandPalette } from './CommandPalette';
import { useShortcuts } from './shortcuts';

interface ShellValue { openPalette: (initial?: string) => void; showShortcuts: () => void }
const ShellContext = createContext<ShellValue>({ openPalette: () => {}, showShortcuts: () => {} });
export const useShell = () => useContext(ShellContext);

export function AppShell({ children }: { children: ReactNode }) {
  const { mspId } = useScope();
  const { session } = useSession();
  const navigate = useNavigate();
  const [palette, setPalette] = useState<{ open: boolean; initial: string }>({ open: false, initial: '' });
  const [help, setHelp] = useState(false);
  const [nav, setNav] = useState(false);
  const openPalette = useCallback((initial = '') => setPalette({ open: true, initial }), []);
  const value = useMemo(() => ({ openPalette, showShortcuts: () => setHelp(true) }), [openPalette]);
  useShortcuts({
    palette: () => setPalette(p => ({ open: !p.open, initial: '' })),
    focusSearch: () => {
      const input = document.querySelector<HTMLInputElement>('[data-page-search]');
      input?.focus();
      return !!input;
    },
    help: () => setHelp(true),
    go: key => {
      const to = { t: 'today', d: 'devices', c: 'customers', w: 'work', a: 'ask', s: 'settings' }[key];
      if (to) navigate(`/w/${mspId}/${to}`);
    },
  });

  return (
    <ShellContext.Provider value={value}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-md focus:bg-panel focus:px-3 focus:py-2 focus:shadow-pop">Skip to content</a>
      <div className="min-h-dvh">
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-[240px] flex-col border-r border-line bg-sunken/70 lg:flex">
          <Sidebar />
        </aside>
        <Dialog.Root open={nav} onOpenChange={setNav}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 animate-fade lg:hidden" />
            <Dialog.Content aria-describedby={undefined} className="fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-r border-line bg-canvas shadow-pop animate-sheet lg:hidden">
              <Dialog.Title className="sr-only">Navigation</Dialog.Title>
              <Dialog.Close className="absolute right-3 top-4 rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close navigation"><X className="size-4" /></Dialog.Close>
              <Sidebar onNavigate={() => setNav(false)} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <div className="min-w-0 lg:pl-[240px]">
          <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-line bg-canvas/85 px-3 backdrop-blur-md sm:px-5 lg:px-8">
            <button type="button" onClick={() => setNav(true)} aria-label="Open navigation" className="rounded-lg p-2 text-ink-2 hover:bg-hover lg:hidden"><Menu className="size-5" /></button>
            <Link to={`/w/${mspId}/today`} className="mr-1 lg:hidden" aria-label="Today"><Logo className="size-7" /></Link>
            <button type="button" onClick={() => openPalette()}
              className="group flex h-9 w-full min-w-0 max-w-xl items-center gap-2.5 rounded-lg border border-line bg-panel px-3 text-left text-ink-3 shadow-card transition-colors hover:border-line-strong">
              <Search className="size-4 shrink-0" />
              <span className="flex-1 truncate"><span className="sm:hidden">Search or ask…</span><span className="hidden sm:inline">Search devices, customers, or ask a question…</span></span>
              <span className="hidden items-center gap-1 sm:flex"><Kbd>{modKey}</Kbd><Kbd>K</Kbd></span>
            </button>
            <div className="flex-1" />
            {session.dataset === 'synthetic' && (
              <Link to={`/w/${mspId}/settings#sources`} title="Invented records for local development. None of it is customer data."
                className="shrink-0 rounded-full bg-attention-soft px-2.5 py-1 text-[12px] font-semibold text-attention hover:underline">Demo data</Link>
            )}
            <button type="button" onClick={() => setHelp(true)} aria-label="Keyboard shortcuts"
              className="hidden rounded-lg p-2 text-ink-3 hover:bg-hover hover:text-ink sm:block"><Keyboard className="size-[18px]" /></button>
          </header>
          <main id="main" className="mx-auto w-full max-w-[1400px] px-4 pb-16 pt-6 sm:px-6 lg:px-8">{children}</main>
        </div>
      </div>
      <CommandPalette open={palette.open} initial={palette.initial} onOpenChange={open => setPalette(p => ({ ...p, open }))} />
      <ShortcutsDialog open={help} onOpenChange={setHelp} />
    </ShellContext.Provider>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { mspId } = useScope();
  const { session } = useSession();
  const demo = session.dataset === 'synthetic';
  const tickets = useTicketSummary(!!session.serviceDesk);
  const needs = useSearch(needsLookQuery(), { keepPrevious: true });
  const fleet = useFacets(fleetQuery);
  const missingSources = useMissingSources();
  const { catalog, isSuccess: hasCatalog } = useCatalog();
  const jobs = useJobs();
  const approvals = jobs.data?.filter(j => j.state === 'awaiting_approval').length;
  const managesEndUsers = useManagesEndUsers();
  const items = [
    { to: 'today', label: 'Today', icon: Activity, count: needs.data?.total, tone: 'attention' as const },
    { to: 'devices', label: 'Devices', icon: Monitor, count: fleet.data?.total },
    { to: 'customers', label: 'Customers', icon: Building2, count: hasCatalog ? catalog.organizations.length : undefined },
    ...(managesEndUsers ? [{ to: 'end-users', label: 'End users', icon: UserRound }] : []),
    ...(session.serviceDesk ? [{ to: 'tickets', label: 'Tickets', icon: ClipboardList, count: tickets.data?.unread || undefined }] : []),
    { to: 'work', label: 'Work', icon: ClipboardList, count: approvals || undefined, tone: 'attention' as const },
    { to: 'scripts', label: 'Scripts', icon: FileCode2 },
    { to: 'audit', label: 'Audit', icon: ScrollText },
  ];
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2.5 px-4">
        <Logo className="size-7" />
        <span className="text-[15px] font-semibold tracking-tight">WITTCO <span className="font-normal text-ink-3">RMM</span></span>
      </div>
      <div className="px-3"><ScopeSwitcher onNavigate={onNavigate} /></div>
      <nav aria-label="Primary" className="mt-4 space-y-0.5 px-3">
        {items.map(item => (
          <NavLink key={item.to} to={`/w/${mspId}/${item.to}`} onClick={onNavigate}
            className={({ isActive }) => cn('group flex h-9 items-center gap-3 rounded-lg px-2.5 font-medium transition-colors',
              isActive ? 'bg-panel text-ink shadow-card ring-1 ring-line' : 'text-ink-2 hover:bg-hover hover:text-ink')}>
            {({ isActive }) => (<>
              <item.icon className={cn('size-[18px]', isActive ? 'text-brand' : 'text-ink-3 group-hover:text-ink-2')} strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {item.count !== undefined && (
                <span className={cn('tabular rounded-md px-1.5 text-[12px]', item.tone === 'attention' && item.count > 0
                  ? 'bg-attention-soft font-semibold text-attention' : 'text-ink-3')}>{item.count}</span>
              )}
            </>)}
          </NavLink>
        ))}
      </nav>
      <div className="flex-1" />
      <Link to={`/w/${mspId}/settings#sources`} onClick={onNavigate}
        className="mx-3 mb-2 block rounded-lg border border-line bg-panel/60 px-3 py-2.5 text-[12.5px] transition-colors hover:border-line-strong">
        <span className="flex items-center gap-2 font-medium text-ink">
          <span className={cn('size-1.5 rounded-full', demo ? 'bg-attention' : fleet.data?.agent.managed ? 'bg-healthy' : 'bg-ink-3')} />
          {fleet.data ? `${fleet.data.agent.managed} of ${fleet.data.total} devices reporting` : 'Device agent'}
        </span>
        <span className="mt-0.5 block text-ink-3">{demo && 'Synthetic demo: invented records, simulated agents. '}{missingSources.length} sources not connected yet</span>
      </Link>
      <div className="border-t border-line px-3 py-2.5"><UserMenu onNavigate={onNavigate} /></div>
    </div>
  );
}

function ScopeSwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const { mspId, mspName, coverage } = useScope();
  const workspaces = useWorkspaces();
  const navigate = useNavigate();
  const label = (
    <span className="min-w-0 flex-1 text-left">
      <span className="block truncate font-semibold text-ink">{mspName}</span>
      <span className="block truncate text-[12px] text-ink-3">{coverage}</span>
    </span>
  );
  const base = 'flex w-full items-center gap-2 rounded-lg border border-line bg-panel px-2.5 py-2 shadow-card';
  if (workspaces.length < 2) return <div className={base} title="Your workspace">{label}</div>;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={cn(base, 'transition-colors hover:border-line-strong')} aria-label={`Workspace: ${mspName}. Switch workspace`}>
        {label}<ChevronsUpDown className="size-4 shrink-0 text-ink-3" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className="z-50 w-[var(--radix-dropdown-menu-trigger-width)] min-w-60 rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
          <DropdownMenu.Label className="px-2.5 pb-1 pt-1.5 text-[12px] font-medium text-ink-3">Switch workspace</DropdownMenu.Label>
          {workspaces.map(w => (
            <DropdownMenu.Item key={w.id} onSelect={() => { if (w.id !== mspId) { navigate(`/w/${w.id}/today`); onNavigate?.(); } }}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-hover">
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{w.name}</span><span className="block truncate text-[12px] text-ink-3">{w.coverage}</span></span>
              {w.id === mspId && <Check className="size-4 text-brand" />}
            </DropdownMenu.Item>
          ))}
          <p className="px-2.5 pb-1.5 pt-1 text-[11.5px] leading-snug text-ink-3">Switching clears results, selections and briefs from the current workspace.</p>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function UserMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { session, signOut, signingOut } = useSession();
  const { role, mspId } = useScope();
  const { showShortcuts } = useShell();
  const [theme, setTheme] = useTheme();
  const navigate = useNavigate();
  const themes: Array<[Theme, string, typeof Sun]> = [['system', 'System', SunMoon], ['light', 'Light', Sun], ['dark', 'Dark', Moon]];
  const item = 'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-hover';
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left hover:bg-hover">
        <span className="flex size-8 items-center justify-center rounded-full bg-ink text-[12px] font-semibold text-canvas">{role[0]}</span>
        <span className="min-w-0 flex-1"><span className="block text-[13px] font-medium">Signed in</span><span className="block text-[12px] text-ink-3">{role}</span></span>
        <Settings className="size-4 text-ink-3" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content side="top" align="start" sideOffset={8} className="z-50 w-60 rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
          <div className="px-2.5 py-2 text-[12px] text-ink-3">Session ends {absolute(session.expiresAt)}</div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item className={item} onSelect={() => { navigate(`/w/${mspId}/settings`); onNavigate?.(); }}><Settings className="size-4 text-ink-3" />Settings</DropdownMenu.Item>
          <DropdownMenu.Item className={item} onSelect={showShortcuts}><Keyboard className="size-4 text-ink-3" />Keyboard shortcuts</DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.RadioGroup value={theme} onValueChange={v => setTheme(v as Theme)}>
            {themes.map(([value, label, Icon]) => (
              <DropdownMenu.RadioItem key={value} value={value} className={item} onSelect={e => e.preventDefault()}>
                <Icon className="size-4 text-ink-3" />{label}
                <DropdownMenu.ItemIndicator className="ml-auto"><Check className="size-4 text-brand" /></DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item className={item} disabled={signingOut} onSelect={() => void signOut()}>
            <LogOut className="size-4 text-ink-3" />{signingOut ? 'Signing out…' : 'Sign out'}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const rows: Array<[string[], string]> = [
    [[modKey, 'K'], 'Search or ask anything'], [['/'], 'Focus the page’s filter box'], [['↑', '↓'], 'Move through results'],
    [['Enter'], 'Open the selected result'], [['Esc'], 'Close a panel or preview'], [['G', 'T'], 'Go to Today'],
    [['G', 'D'], 'Go to Devices'], [['G', 'C'], 'Go to Customers'], [['G', 'W'], 'Go to Work'], [['G', 'A'], 'Go to Ask'], [['?'], 'Show this list'],
  ];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content aria-describedby={undefined} className="fixed left-1/2 top-[15vh] z-50 w-[min(420px,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-line bg-raised p-5 shadow-pop animate-pop">
          <Dialog.Title className="text-base font-semibold">Keyboard shortcuts</Dialog.Title>
          <dl className="mt-4 space-y-2.5">
            {rows.map(([keys, label]) => (
              <div key={label} className="flex items-center justify-between gap-4">
                <dt className="text-ink-2">{label}</dt>
                <dd className="flex gap-1">{keys.map(k => <Kbd key={k}>{k}</Kbd>)}</dd>
              </div>
            ))}
          </dl>
          <Dialog.Close className="absolute right-4 top-4 rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
